// Curated, indexable destination guides — hand-written to convert readers into
// WanderlyMe planner users. Each guide is the single source of truth used by the
// React pages AND the build-time prerenderer + sitemap generator.
//
// Schema per destination:
//   slug, name, country, region, tagline, image, updated (YYYY-MM-DD),
//   tripLength, bestTime,
//   budget: { budget, mid, luxury }  — per person, per day
//   intro, areas:[{name,detail}], highlights:[str], itinerary:[{day,title,detail}], tips:[str]

export const DESTINATIONS = [
  {
    slug: "lisbon-portugal",
    name: "Lisbon",
    country: "Portugal",
    region: "Europe",
    tagline: "Sun-washed hills, tram bells and pastéis de nata",
    image:
      "https://images.unsplash.com/photo-1585208798174-6cedd86e019a?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-08-29",
    tripLength: "3–4 days",
    bestTime: "March–May and September–October",
    budget: { budget: "€55–80 / day", mid: "€110–170 / day", luxury: "€280+ / day" },
    intro:
      "Lisbon layers seven hills with pastel façades, azulejo tiles and a river that glows gold at sunset. It is one of Europe's most affordable capitals, rewarding slow wandering through Alfama's alleys, history in Belém, and long dinners of grilled sardines and vinho verde. Trams rattle up impossibly steep lanes, miradouros (viewpoints) frame the Tagus, and a strong café culture makes it easy to slow down.",
    areas: [
      { name: "Alfama", detail: "The oldest quarter — tangled lanes, fado houses and washing strung between tiled walls. Best for atmosphere and sunset viewpoints." },
      { name: "Baixa & Chiado", detail: "Grand squares and elegant shopping streets rebuilt after the 1755 earthquake. Central and walkable — a smart base." },
      { name: "Belém", detail: "Riverside monuments, the original pastéis de Belém and the age-of-discovery landmarks, a short tram ride west." },
      { name: "Bairro Alto & Príncipe Real", detail: "Sleepy by day, buzzing by night — bars, boutiques and the city's best rooftop drinks." },
    ],
    highlights: [
      "Ride Tram 28 through Alfama and Graça",
      "Sunset from Miradouro da Senhora do Monte",
      "Jerónimos Monastery and the original pastéis de Belém",
      "Day trip to the palaces of Sintra",
      "Time Out Market for a tasting tour of Portuguese food",
    ],
    itinerary: [
      { day: 1, title: "Old Lisbon", detail: "Alfama alleys, Sé Cathedral, São Jorge Castle views, and a fado dinner in a tiny tavern." },
      { day: 2, title: "Belém & the river", detail: "Jerónimos Monastery, Belém Tower, custard tarts, then MAAT and a riverside sunset." },
      { day: 3, title: "Sintra escape", detail: "Train to Sintra for Pena Palace and Quinta da Regaleira's gardens; back for drinks in Bairro Alto." },
    ],
    tips: [
      "Buy a rechargeable Viva Viagem card for trams, metro and the Cascais/Sintra trains.",
      "Wear grippy shoes — the calçada (mosaic pavement) is beautiful but slippery on the hills.",
      "Book Sintra's Pena Palace tickets online; queues on site can swallow an hour.",
      "Lunch is the best-value meal — many restaurants run a prato do dia (dish of the day).",
    ],
  },
  {
    slug: "kyoto-japan",
    name: "Kyoto",
    country: "Japan",
    region: "Asia",
    tagline: "Temples, tea houses and bamboo light",
    image:
      "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-08-29",
    tripLength: "3–5 days",
    bestTime: "Late March–April (blossoms) and November (autumn leaves)",
    budget: { budget: "¥9,000–13,000 / day", mid: "¥18,000–30,000 / day", luxury: "¥55,000+ / day" },
    intro:
      "Japan's former imperial capital keeps a thousand years of craft alive across 1,600 temples, geisha districts and kaiseki kitchens. Mornings belong to quiet shrines; evenings to lantern-lit Gion and riverside dining on the Kamogawa. Kyoto rewards early starts and slow tea — it is a city to feel rather than tick off.",
    areas: [
      { name: "Higashiyama", detail: "Preserved lanes climbing to Kiyomizu-dera — the postcard Kyoto of wooden shopfronts and lantern light." },
      { name: "Gion", detail: "The famous geisha district; teahouses, the Kamogawa riverbank and Kyoto's most atmospheric evening stroll." },
      { name: "Arashiyama", detail: "Western edge of the city — bamboo grove, riverside temples and the monkey park, best at dawn." },
      { name: "Downtown (Kawaramachi/Nishiki)", detail: "Central, transit-friendly base with Nishiki Market, department-store food halls and easy bus links." },
    ],
    highlights: [
      "Vermilion torii gates at Fushimi Inari",
      "Arashiyama bamboo grove at dawn",
      "Kinkaku-ji, the Golden Pavilion",
      "Matcha and wagashi in a Gion tea house",
      "Nishiki Market food crawl",
    ],
    itinerary: [
      { day: 1, title: "Southern shrines", detail: "Fushimi Inari at sunrise before crowds, then Tofuku-ji's gardens and Nishiki Market for lunch." },
      { day: 2, title: "Arashiyama", detail: "Bamboo grove, Tenryu-ji, the monkey park views, then a riverside boat or an onsen soak." },
      { day: 3, title: "Gion & tea", detail: "Kiyomizu-dera, Ninenzaka lanes, a tea ceremony, and an evening walk through Gion." },
    ],
    tips: [
      "Get an ICOCA card and use buses/subway; a one-day bus pass pays off fast.",
      "Temples open early (often 6–8am) — beat the crowds and the heat by starting at dawn.",
      "Carry cash: many small temples, teahouses and shrines don't take cards.",
      "Be discreet in Gion — photographing geiko/maiko without consent is fined; stick to public streets.",
    ],
  },
  {
    slug: "reykjavik-iceland",
    name: "Reykjavík",
    country: "Iceland",
    region: "Europe",
    tagline: "Northern lights, lava fields and geothermal spas",
    image:
      "https://images.unsplash.com/photo-1504893524553-b855bce32c67?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-08-29",
    tripLength: "3–4 days",
    bestTime: "June–August (midnight sun) or September–March (auroras)",
    budget: { budget: "€90–130 / day", mid: "€170–260 / day", luxury: "€400+ / day" },
    intro:
      "The world's northernmost capital is a launchpad for waterfalls, glaciers and steaming spas. Compact and colourful, Reykjavík pairs Nordic design and fresh seafood with easy day trips along the Golden Circle. Iceland is pricey, but the landscapes are otherworldly — and a rental car turns a city break into an epic road trip.",
    areas: [
      { name: "Downtown (101)", detail: "Laugavegur shopping street, Hallgrímskirkja and the old harbour — everything walkable, most stays here." },
      { name: "Old Harbour", detail: "Whale-watching and puffin tours, seafood shacks and the Grandi district's museums and food hall." },
      { name: "Golden Circle (day-trip belt)", detail: "Þingvellir, Geysir and Gullfoss — the classic loop 1–2 hours east of the city." },
      { name: "Reykjanes Peninsula", detail: "Lava fields, the Blue Lagoon and the airport — good for the first or last day." },
    ],
    highlights: [
      "Golden Circle: Þingvellir, Geysir, Gullfoss",
      "Soak in the Blue Lagoon or Sky Lagoon",
      "Hallgrímskirkja's tower views",
      "Chase the aurora on a clear winter night",
      "Whale watching from the old harbour",
    ],
    itinerary: [
      { day: 1, title: "City & spa", detail: "Harpa, Hallgrímskirkja and the harbour, then an evening soak at Sky Lagoon over the sea." },
      { day: 2, title: "Golden Circle", detail: "Þingvellir rift, erupting Geysir, thundering Gullfoss, with a stop at a tomato-farm café." },
      { day: 3, title: "South coast taste", detail: "Seljalandsfoss and Skógafoss waterfalls and Reynisfjara's black-sand beach." },
    ],
    tips: [
      "Renting a car unlocks far more than any bus tour — book early in summer.",
      "Fill a water bottle from the tap; Icelandic water is superb and free.",
      "Aurora needs dark, clear skies (Sept–Mar) — check the vedur.is forecast nightly.",
      "Save money by eating the famous hot dogs and shopping at Bónus supermarkets.",
    ],
  },
  {
    slug: "porto-portugal",
    name: "Porto",
    country: "Portugal",
    region: "Europe",
    tagline: "Port cellars, tiled churches and river bridges",
    image:
      "https://images.unsplash.com/photo-1555881400-74d7acaacd8b?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-08-29",
    tripLength: "2–3 days",
    bestTime: "May–June and September",
    budget: { budget: "€50–75 / day", mid: "€100–160 / day", luxury: "€260+ / day" },
    intro:
      "Porto tumbles down to the Douro in a jumble of granite, azulejos and iron bridges. Cross to Vila Nova de Gaia for port tastings, then feast on a francesinha before a river cruise. It is smaller and steeper than Lisbon, wonderfully atmospheric, and the gateway to the terraced vineyards of the Douro Valley.",
    areas: [
      { name: "Ribeira", detail: "The UNESCO riverfront — colourful houses, cafés under the arches and the departure point for river cruises." },
      { name: "Baixa & Aliados", detail: "Grand avenue, São Bento station's tiled hall and the Lello bookshop — the walkable centre." },
      { name: "Vila Nova de Gaia", detail: "Across the river: the port lodges, tasting cellars and the best views back at Porto's skyline." },
      { name: "Foz do Douro", detail: "Where the river meets the Atlantic — seaside promenades, gardens and sunset drinks." },
    ],
    highlights: [
      "Livraria Lello and São Bento's tiled hall",
      "Port tasting in the Gaia cellars",
      "Dom Luís I Bridge at golden hour",
      "Douro Valley wine day trip",
      "Francesinha at a classic tasca",
    ],
    itinerary: [
      { day: 1, title: "Ribeira", detail: "São Bento, Clérigos tower, the Ribeira lanes and a sunset cruise under the six bridges." },
      { day: 2, title: "Port & Gaia", detail: "Cross the bridge for cellar tastings, the cable car and skyline views, then dinner riverside." },
      { day: 3, title: "Douro Valley", detail: "Train or tour up the terraced Douro for vineyard visits and a slow river lunch." },
    ],
    tips: [
      "Walk the lower deck of Dom Luís I Bridge for photos, the upper deck for the metro and views.",
      "Many port lodges offer combined tasting flights — book a couple, don't try to do all.",
      "The Douro train line from São Bento is one of Europe's prettiest and very cheap.",
      "Sundays: some sights close early — check hours before planning your last day.",
    ],
  },
  {
    slug: "seville-spain",
    name: "Seville",
    country: "Spain",
    region: "Europe",
    tagline: "Flamenco, orange trees and Moorish palaces",
    image:
      "https://images.unsplash.com/photo-1559386081-325882507af7?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-08-29",
    tripLength: "2–3 days",
    bestTime: "March–May and October–November",
    budget: { budget: "€55–80 / day", mid: "€110–170 / day", luxury: "€280+ / day" },
    intro:
      "Andalusia's capital is all courtyards, tapas and heat. The Real Alcázar's gardens, a soaring cathedral and the curves of Plaza de España set the stage for late nights of flamenco and jamón. Seville is compact and made for wandering — but plan around the fierce summer sun by exploring early and resting through the afternoon.",
    areas: [
      { name: "Santa Cruz", detail: "The old Jewish quarter — whitewashed lanes, hidden plazas and the cathedral and Alcázar on its edge." },
      { name: "El Arenal & Centro", detail: "Bullring, riverfront and the main shopping streets; central and well connected." },
      { name: "Triana", detail: "Across the river — ceramics, a lively market and the city's most authentic flamenco bars." },
      { name: "Alameda", detail: "Bohemian, leafy and buzzing at night with the young Sevillano crowd." },
    ],
    highlights: [
      "Real Alcázar palace and gardens",
      "Climb the Giralda for rooftop views",
      "Sunset at Plaza de España",
      "Tapas crawl through Triana",
      "Live flamenco in an intimate tablao",
    ],
    itinerary: [
      { day: 1, title: "Old town", detail: "Cathedral, the Giralda climb, and the Alcázar's Mudéjar halls and gardens." },
      { day: 2, title: "Triana & tapas", detail: "Plaza de España, the Triana market and a river walk, then an evening flamenco show." },
      { day: 3, title: "Slow Seville", detail: "Metropol Parasol viewpoint, Casa de Pilatos, and a long lunch of jamón and salmorejo." },
    ],
    tips: [
      "Book the Real Alcázar online with a timed slot — walk-up queues are long.",
      "Eat late like locals: dinner rarely starts before 9pm.",
      "For real flamenco, choose a small tablao or a peña over the big tourist dinner shows.",
      "Avoid July–August unless you love 40°C heat; spring and autumn are magical.",
    ],
  },
  {
    slug: "tbilisi-georgia",
    name: "Tbilisi",
    country: "Georgia",
    region: "Europe",
    tagline: "Sulphur baths, wine and crossroads cuisine",
    image:
      "https://images.unsplash.com/photo-1565008576549-57569a49371d?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-08-29",
    tripLength: "3–4 days",
    bestTime: "May–June and September–October",
    budget: { budget: "$25–40 / day", mid: "$55–90 / day", luxury: "$160+ / day" },
    intro:
      "Where Europe meets Asia, Tbilisi mixes cobbled old-town balconies, cutting-edge design and 8,000 years of winemaking. Soak in the Abanotubani sulphur baths and feast on khinkali and khachapuri. It is one of the best-value city breaks anywhere — warm, walkable and a springboard for dramatic mountain and cave-city day trips.",
    areas: [
      { name: "Old Town (Kala)", detail: "Carved wooden balconies, the sulphur bath domes and Narikala fortress on the ridge above." },
      { name: "Sololaki & Rustaveli", detail: "Grand avenues, art-nouveau facades, museums and the leafy heart of the modern city." },
      { name: "Abanotubani", detail: "The bath district — brick domes over natural sulphur springs, the classic Tbilisi soak." },
      { name: "Fabrika / Marjanishvili", detail: "A former sewing factory turned hostel-and-bar hub; the city's creative, nightlife-friendly side." },
    ],
    highlights: [
      "Abanotubani sulphur bath district",
      "Narikala Fortress cable car and views",
      "Wine and a supra feast of khachapuri",
      "Day trip to cave-city Uplistsikhe",
      "Dry Bridge flea market finds",
    ],
    itinerary: [
      { day: 1, title: "Old Tbilisi", detail: "Narikala fortress by cable car, a sulphur bath soak, and the balconied lanes at dusk." },
      { day: 2, title: "Wine & history", detail: "Georgian wine tasting and a day trip to Mtskheta's ancient monasteries." },
      { day: 3, title: "Mountains or caves", detail: "Kazbegi's Gergeti Trinity Church or the cave-city of Uplistsikhe." },
    ],
    tips: [
      "Georgian wine is made in clay qvevri — ask for an amber (skin-contact) glass.",
      "Marshrutka minivans are the cheapest way to reach Kazbegi and Mtskheta.",
      "Order khinkali by the piece and eat them by hand — never with a fork.",
      "Cash (lari) is handy for markets and baths, though cards work in the centre.",
    ],
  },
  {
    slug: "rome-italy",
    name: "Rome",
    country: "Italy",
    region: "Europe",
    tagline: "Ancient ruins, baroque squares and endless pasta",
    image:
      "https://images.unsplash.com/photo-1626440409578-8bc4ab03b772?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-08-29",
    tripLength: "3–4 days",
    bestTime: "April–June and September–October",
    budget: { budget: "€70–100 / day", mid: "€140–210 / day", luxury: "€350+ / day" },
    intro:
      "Rome is a living museum where 2,000-year-old ruins share street corners with baroque fountains and buzzing trattorias. The Eternal City rewards a slow morning espresso, an afternoon of ancient wonders, and evenings of cacio e pepe in cobbled Trastevere. Everything is walkable if you pace yourself — and gelato is a legitimate daily strategy.",
    areas: [
      { name: "Centro Storico", detail: "The historic core — Pantheon, Piazza Navona and Trevi Fountain, all within a short stroll." },
      { name: "Trastevere", detail: "Ivy-clad lanes, the city's best casual dining and a lively, romantic evening scene across the river." },
      { name: "Monti", detail: "Hip and central, wedged between the Colosseum and Termini — boutiques, wine bars and a village feel." },
      { name: "Vatican & Prati", detail: "St Peter's and the museums, with elegant, quieter streets and great value trattorias nearby." },
    ],
    highlights: [
      "Colosseum, Roman Forum and Palatine Hill",
      "The Pantheon's open oculus",
      "Vatican Museums and the Sistine Chapel",
      "Toss a coin in the Trevi Fountain",
      "Aperitivo and dinner in Trastevere",
    ],
    itinerary: [
      { day: 1, title: "Ancient Rome", detail: "Colosseum, Forum and Palatine Hill in the morning, then Capitoline views and Monti at night." },
      { day: 2, title: "Baroque heart", detail: "Pantheon, Piazza Navona, Trevi Fountain and the Spanish Steps, ending with gelato and people-watching." },
      { day: 3, title: "Vatican & Trastevere", detail: "St Peter's and the Vatican Museums early, then cross to Trastevere for a long dinner." },
    ],
    tips: [
      "Book Colosseum and Vatican tickets online with timed entry — lines are brutal otherwise.",
      "Refill your bottle at the free 'nasoni' street fountains all over the city.",
      "Avoid restaurants with photo menus by the big sights; walk two streets back for better, cheaper food.",
      "Dress modestly (shoulders and knees covered) to enter St Peter's and churches.",
    ],
  },
  {
    slug: "istanbul-turkey",
    name: "Istanbul",
    country: "Turkey",
    region: "Europe",
    tagline: "Two continents, grand mosques and spice-scented bazaars",
    image:
      "https://images.unsplash.com/photo-1527838832700-5059252407fa?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-08-29",
    tripLength: "3–4 days",
    bestTime: "April–May and September–November",
    budget: { budget: "$35–55 / day", mid: "$75–120 / day", luxury: "$220+ / day" },
    intro:
      "Straddling Europe and Asia across the Bosphorus, Istanbul stacks Byzantine and Ottoman grandeur beside a thumping modern city. Call to prayer echoes over the domes of Sultanahmet, ferries crisscross the strait, and the food — from simit to slow-cooked kebabs — is reason enough to visit. It's big, layered and endlessly rewarding.",
    areas: [
      { name: "Sultanahmet", detail: "The historic peninsula — Hagia Sophia, Blue Mosque, Topkapı Palace and the Grand Bazaar. Sight-dense, touristy." },
      { name: "Beyoğlu & Karaköy", detail: "Across the Golden Horn: İstiklal Street, galleries, rooftop bars and the city's coolest cafés." },
      { name: "Kadıköy (Asian side)", detail: "A local, foodie neighbourhood of markets and meyhanes — a short, scenic ferry away." },
      { name: "Beşiktaş & Ortaköy", detail: "Bosphorus-front dining under the bridge, waterside mosques and lively weekends." },
    ],
    highlights: [
      "Hagia Sophia and the Blue Mosque",
      "Topkapı Palace and its harem",
      "Bargain in the Grand and Spice Bazaars",
      "A Bosphorus ferry between two continents",
      "A traditional hammam steam and scrub",
    ],
    itinerary: [
      { day: 1, title: "Imperial Sultanahmet", detail: "Hagia Sophia, Blue Mosque, the Basilica Cistern and the Grand Bazaar, ending with a hammam." },
      { day: 2, title: "Palaces & the strait", detail: "Topkapı Palace, the Spice Bazaar, then a Bosphorus ferry cruise at golden hour." },
      { day: 3, title: "Modern Istanbul", detail: "İstiklal Street and Galata Tower, then ferry to Kadıköy for market food and meyhane dinner." },
    ],
    tips: [
      "Get an İstanbulkart for cheap trams, ferries and buses across both sides.",
      "Mosques close to tourists during the five daily prayers — check times and dress modestly.",
      "The scenic public ferries cost a fraction of tourist 'cruises' and go the same way.",
      "Agree a price before any 'free' tea or shoe-shine, and haggle politely in the bazaars.",
    ],
  },
  {
    slug: "prague-czech-republic",
    name: "Prague",
    country: "Czech Republic",
    region: "Europe",
    tagline: "Gothic spires, a fairytale castle and cheap great beer",
    image:
      "https://images.unsplash.com/photo-1600623471616-8c1966c91ff6?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-08-29",
    tripLength: "2–3 days",
    bestTime: "April–June and September–October",
    budget: { budget: "€45–70 / day", mid: "€95–150 / day", luxury: "€260+ / day" },
    intro:
      "Prague survived the 20th century almost untouched, leaving a storybook centre of Gothic towers, baroque palaces and the largest ancient castle complex in the world. Cross the statue-lined Charles Bridge at dawn, get lost in the old town's lanes, and refuel on hearty Czech food and some of the best-value beer in Europe.",
    areas: [
      { name: "Staré Město (Old Town)", detail: "The astronomical clock, the main square and a maze of lanes — the heart, but busy by midday." },
      { name: "Malá Strana", detail: "Below the castle: baroque palaces, quiet gardens and the prettiest approach to Charles Bridge." },
      { name: "Hradčany (Castle District)", detail: "Prague Castle, St Vitus Cathedral and sweeping views over the red rooftops." },
      { name: "Vinohrady", detail: "Leafy, residential and local — great cafés, wine bars and a calmer place to stay." },
    ],
    highlights: [
      "Charles Bridge at sunrise",
      "Prague Castle and St Vitus Cathedral",
      "The Old Town astronomical clock",
      "The bohemian Lennon Wall and Kampa island",
      "A traditional Czech beer hall",
    ],
    itinerary: [
      { day: 1, title: "Old Town", detail: "Old Town Square and clock, the Jewish Quarter, and Charles Bridge as the lights come on." },
      { day: 2, title: "Castle & Malá Strana", detail: "Prague Castle and St Vitus early, then descend through gardens to Kampa and a riverside beer." },
      { day: 3, title: "Local Prague", detail: "Vyšehrad fortress views, Vinohrady cafés, and a farewell dinner of svíčková and Pilsner." },
    ],
    tips: [
      "Cross Charles Bridge before 8am or after dark to escape the crush.",
      "Change money only at reputable exchanges (or ATMs) — avoid '0% commission' booths with bad rates.",
      "Trams are cheap and scenic; tram 22 is a rolling sightseeing route to the castle.",
      "Tip ~10% in restaurants and check the bill for a service charge first.",
    ],
  },
  {
    slug: "marrakech-morocco",
    name: "Marrakech",
    country: "Morocco",
    region: "Africa",
    tagline: "Souks, riads and the drama of the Djemaa el-Fna",
    image:
      "https://images.unsplash.com/photo-1596750320291-a082a23dcc19?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-08-29",
    tripLength: "3–4 days",
    bestTime: "March–May and October–November",
    budget: { budget: "$30–50 / day", mid: "$70–130 / day", luxury: "$280+ / day" },
    intro:
      "Marrakech is a sensory rush: labyrinthine souks, hidden riads with plunge pools, and a main square that transforms nightly into a theatre of food stalls and storytellers. Behind the red walls lies calm — tiled courtyards, rooftop mint tea and gardens designed for shade. Base yourself in a riad and let the medina reveal itself slowly.",
    areas: [
      { name: "The Medina", detail: "The walled old city — souks, riads and the Djemaa el-Fna square. Chaotic, magical, and where most stay." },
      { name: "Djemaa el-Fna", detail: "The pulsing main square: juice stalls by day, food grills and performers by night. Rooftop cafés ring it." },
      { name: "Gueliz (Ville Nouvelle)", detail: "The modern quarter — wide boulevards, boutiques, galleries and the Jardin Majorelle nearby." },
      { name: "Palmeraie", detail: "Palm groves on the edge of town with resort hotels and quieter, spread-out luxury stays." },
    ],
    highlights: [
      "Sunset over the Djemaa el-Fna",
      "Jardin Majorelle and the YSL Museum",
      "Bahia Palace and the Saadian Tombs",
      "Haggle through the colour-drenched souks",
      "A hammam and mint tea on a riad rooftop",
    ],
    itinerary: [
      { day: 1, title: "Into the medina", detail: "Bahia Palace, the souks and Ben Youssef Madrasa, then dinner at the food stalls on Djemaa el-Fna." },
      { day: 2, title: "Gardens & style", detail: "Jardin Majorelle and the YSL Museum, a hammam in the afternoon, and rooftop drinks at sunset." },
      { day: 3, title: "Atlas escape", detail: "Day trip to the Atlas Mountains or the Agafay desert for tagine with a view." },
    ],
    tips: [
      "Agree taxi fares before getting in, or insist on the meter ('compteur').",
      "Haggling is expected in the souks — start around a third of the asking price and stay friendly.",
      "Carry small dirham notes for tips, tea and photos; many stalls are cash-only.",
      "Hire a licensed guide for your first medina walk — the lanes are genuinely maze-like.",
    ],
  },
  {
    slug: "mexico-city-mexico",
    name: "Mexico City",
    country: "Mexico",
    region: "North America",
    tagline: "World-class food, muralist art and Aztec history",
    image:
      "https://images.unsplash.com/photo-1547686669-9a8cb1a22d91?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-08-29",
    tripLength: "4–5 days",
    bestTime: "March–May and October–November (dry season)",
    budget: { budget: "$35–55 / day", mid: "$80–140 / day", luxury: "$300+ / day" },
    intro:
      "CDMX is one of the planet's great food and culture capitals — leafy walkable neighbourhoods, Aztec ruins beside colonial cathedrals, and a taco on every corner that could ruin you for taquerias elsewhere. It's huge and high (2,240m), so pace yourself: mornings for sights, afternoons for markets and mezcal.",
    areas: [
      { name: "Roma & Condesa", detail: "Tree-lined art-deco streets, the best cafés and restaurants — the traveller's favourite base." },
      { name: "Centro Histórico", detail: "The Zócalo, cathedral, Templo Mayor ruins and Bellas Artes — the grand, historic heart." },
      { name: "Coyoacán", detail: "Cobbled southern village with the Frida Kahlo house, markets and a bohemian, calmer feel." },
      { name: "Polanco", detail: "Upscale dining and museums (Soumaya, Chapultepec) — the polished, high-end quarter." },
    ],
    highlights: [
      "Teotihuacán pyramids day trip",
      "Frida Kahlo's Casa Azul in Coyoacán",
      "The murals of Palacio de Bellas Artes and Diego Rivera",
      "A taco and mezcal crawl through Roma",
      "A boat on the canals of Xochimilco",
    ],
    itinerary: [
      { day: 1, title: "Historic centre", detail: "Zócalo, cathedral, Templo Mayor and Bellas Artes, then rooftop views over the square." },
      { day: 2, title: "Teotihuacán", detail: "Early to the pyramids of the Sun and Moon, back for tacos and mezcal in Roma." },
      { day: 3, title: "Frida & canals", detail: "Casa Azul in Coyoacán, then a colourful trajinera boat on the Xochimilco canals." },
    ],
    tips: [
      "Use Uber/DiDi and the metro rather than hailing street taxis at night.",
      "Drink bottled or filtered water; street food is fantastic — pick busy, high-turnover stalls.",
      "Take it easy on day one — the altitude tires you and dulls the appetite until you adjust.",
      "Book the Frida Kahlo museum online in advance; timed tickets sell out.",
    ],
  },
  {
    slug: "hanoi-vietnam",
    name: "Hanoi",
    country: "Vietnam",
    region: "Asia",
    tagline: "Street-food heaven, lakes and old-quarter chaos",
    image:
      "https://images.unsplash.com/photo-1721222847140-51f6297895c3?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-08-29",
    tripLength: "3–4 days",
    bestTime: "October–April (cooler and drier)",
    budget: { budget: "$20–35 / day", mid: "$50–90 / day", luxury: "$180+ / day" },
    intro:
      "Vietnam's thousand-year-old capital is a joyful tangle of scooters, lakes and legendary street food. The Old Quarter's narrow guild streets spill over with pho, bun cha and egg coffee, while French-colonial boulevards and tranquil lakes give you room to breathe. It's also the gateway to the limestone seascape of Halong Bay.",
    areas: [
      { name: "Old Quarter", detail: "36 ancient guild streets of food stalls, shops and buzzing chaos — the atmospheric place to stay." },
      { name: "Hoan Kiem Lake", detail: "The city's serene green heart; a weekend walking street and the pretty Ngoc Son Temple." },
      { name: "French Quarter", detail: "Wide boulevards, the Opera House and colonial-era cafés — elegant and calmer." },
      { name: "Tay Ho (West Lake)", detail: "Lakeside cafés, expat restaurants and sunset views — a relaxed base away from the honking." },
    ],
    highlights: [
      "A street-food crawl (pho, bun cha, egg coffee)",
      "Hoan Kiem Lake and Ngoc Son Temple",
      "The Temple of Literature",
      "Ho Chi Minh Mausoleum complex",
      "Overnight cruise to Halong Bay",
    ],
    itinerary: [
      { day: 1, title: "Old Quarter", detail: "Graze the food streets, walk Hoan Kiem Lake, and catch a water-puppet show in the evening." },
      { day: 2, title: "History & coffee", detail: "Temple of Literature, the Ho Chi Minh complex, and egg coffee in a hidden French-quarter café." },
      { day: 3, title: "Halong Bay", detail: "Full-day or overnight cruise among the limestone karsts and hidden caves." },
    ],
    tips: [
      "Crossing the road: walk slowly and steadily — the scooters flow around you.",
      "Grab (ride-hailing) is cheap and avoids fare haggling; motorbike taxis are fastest in traffic.",
      "Sit on tiny plastic stools at busy stalls for the best, safest street food.",
      "Book Halong Bay cruises through a reputable operator and check the boat's reviews.",
    ],
  },
  {
    slug: "bali-ubud-indonesia",
    name: "Ubud, Bali",
    country: "Indonesia",
    region: "Asia",
    tagline: "Rice terraces, temples and jungle wellness",
    image:
      "https://images.unsplash.com/photo-1559628233-eb1b1a45564b?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-08-29",
    tripLength: "4–6 days",
    bestTime: "April–October (dry season)",
    budget: { budget: "$25–45 / day", mid: "$65–120 / day", luxury: "$300+ / day" },
    intro:
      "Ubud is Bali's cultural and spiritual heart — emerald rice terraces, river-gorge villas, temple ceremonies and a yoga-and-café scene set among the jungle. It's a slower, greener alternative to the beach south, ideal for temple-hopping, waterfalls and long healthy breakfasts. Rent a scooter or driver and the whole of central Bali opens up.",
    areas: [
      { name: "Central Ubud", detail: "Around the Palace and Monkey Forest — markets, restaurants and the walkable core." },
      { name: "Tegallalang", detail: "The famous tiered rice terraces just north — jungle swings, cafés and photo-perfect valleys." },
      { name: "Penestanan & Sayan", detail: "Ridge and river-gorge villas with infinity pools and a quieter, design-led stay." },
      { name: "Campuhan", detail: "The ridge-walk area — a scenic dawn stroll and boutique wellness retreats." },
    ],
    highlights: [
      "Tegallalang rice terraces at sunrise",
      "Sacred Monkey Forest sanctuary",
      "Tirta Empul water temple purification",
      "Waterfalls at Tegenungan and Tibumana",
      "A yoga class and a Balinese massage",
    ],
    itinerary: [
      { day: 1, title: "Ubud town", detail: "Ubud Palace, the art market and Monkey Forest, then the Campuhan Ridge Walk at sunset." },
      { day: 2, title: "Terraces & temples", detail: "Tegallalang terraces at dawn, Tirta Empul water temple, and a jungle waterfall swim." },
      { day: 3, title: "Volcano sunrise", detail: "Mount Batur sunrise trek or a hot-spring soak, then a slow afternoon of wellness." },
    ],
    tips: [
      "Hire a private driver for a full day of sights — it's cheap and covers far more than taxis.",
      "Bring a sarong (or rent one) — required to enter temples.",
      "Carry small cash for terrace 'donations', parking and warungs.",
      "At the Monkey Forest, hide sunglasses, water bottles and loose bags — the macaques are quick.",
    ],
  },
  {
    slug: "cape-town-south-africa",
    name: "Cape Town",
    country: "South Africa",
    region: "Africa",
    tagline: "Table Mountain, penguins and vineyards by the sea",
    image:
      "https://images.unsplash.com/photo-1588455471455-4b28e9ab3cd5?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-08-29",
    tripLength: "4–6 days",
    bestTime: "November–March (warm, dry summer)",
    budget: { budget: "$40–65 / day", mid: "$90–160 / day", luxury: "$350+ / day" },
    intro:
      "Few cities have a setting like Cape Town — a flat-topped mountain plunging to two oceans, beaches, botanical gardens and world-class vineyards all within reach. Add penguins, a dramatic cape drive and a big food-and-wine culture and you have one of the world's great multi-day city breaks. A rental car makes the coast and Winelands effortless.",
    areas: [
      { name: "City Bowl & Gardens", detail: "Under Table Mountain — museums, the Company's Garden, and the buzzy Bo-Kaap and Kloof Street scenes." },
      { name: "V&A Waterfront", detail: "Harbour dining, the aquarium and the Robben Island ferry — polished and family-friendly." },
      { name: "Camps Bay & Atlantic Seaboard", detail: "Palm-lined beaches, sunset bars and the scenic coastal drive beneath the Twelve Apostles." },
      { name: "Constantia & the Winelands", detail: "City vineyards plus Stellenbosch and Franschhoek an hour away — estate lunches and tastings." },
    ],
    highlights: [
      "Cableway or hike up Table Mountain",
      "Penguins at Boulders Beach",
      "Cape of Good Hope and Chapman's Peak drive",
      "Robben Island and District Six history",
      "Wine tasting in Stellenbosch or Franschhoek",
    ],
    itinerary: [
      { day: 1, title: "Table Mountain & city", detail: "Cableway up the mountain early, then Bo-Kaap, the Company's Garden and Kloof Street dinner." },
      { day: 2, title: "Cape Peninsula", detail: "Chapman's Peak drive, Cape of Good Hope, and the Boulders Beach penguins on the way back." },
      { day: 3, title: "Winelands", detail: "A day of estate tastings and a long lunch in Franschhoek or Stellenbosch." },
    ],
    tips: [
      "Climb or ride Table Mountain in the morning — cloud (the 'tablecloth') and wind build later.",
      "Rent a car: the peninsula and Winelands are hard to do well without one.",
      "Stay aware in quiet or after-dark areas and use ride apps at night, like any big city.",
      "Book Robben Island ferries and the cableway online — both sell out and depend on weather.",
    ],
  },
  {
    slug: "queenstown-new-zealand",
    name: "Queenstown",
    country: "New Zealand",
    region: "Oceania",
    tagline: "Alpine lakes, adrenaline and Middle-earth scenery",
    image:
      "https://images.unsplash.com/photo-1547314283-befb6cc5cf29?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-08-29",
    tripLength: "4–6 days",
    bestTime: "December–February (summer) and June–August (ski)",
    budget: { budget: "NZ$90–130 / day", mid: "NZ$180–280 / day", luxury: "NZ$500+ / day" },
    intro:
      "Cradled by the Remarkables mountains on the shore of Lake Wakatipu, Queenstown is New Zealand's adventure capital — bungy, jet boats and hiking in summer, world-class skiing in winter. Beyond the adrenaline lie serene fjords, gold-rush villages and some of the Southern Hemisphere's most cinematic drives, including the road to Milford Sound.",
    areas: [
      { name: "Town Centre", detail: "Lakefront bars, restaurants and the gondola — compact and walkable, the natural base." },
      { name: "Queenstown Hill & Fernhill", detail: "Elevated stays with lake-and-mountain views a short hop from the centre." },
      { name: "Arrowtown", detail: "A preserved gold-rush village 20 minutes away — historic cottages, cafés and autumn colour." },
      { name: "Glenorchy", detail: "The road-trip end of the lake — Lord of the Rings landscapes and trailheads into the wild." },
    ],
    highlights: [
      "Skyline Gondola and luge over the lake",
      "Day trip to Milford Sound",
      "Jet boating the Shotover or a bungy jump",
      "Historic Arrowtown and the Gibbston wineries",
      "Hike or scenic drive to Glenorchy",
    ],
    itinerary: [
      { day: 1, title: "Lake & views", detail: "Skyline Gondola and luge, a lakefront walk, and a TSS Earnslaw steamship cruise at sunset." },
      { day: 2, title: "Milford Sound", detail: "Long but spectacular drive (or fly) to Fiordland for a boat cruise beneath the waterfalls." },
      { day: 3, title: "Adventure or wine", detail: "Jet boat/bungy for thrill-seekers, or Arrowtown and Gibbston Valley wine tasting for a slower day." },
    ],
    tips: [
      "Milford Sound is a 4-hour drive each way — leave early or take a coach so you can enjoy the views.",
      "Book adventure activities and Milford cruises ahead in summer; they sell out.",
      "Weather changes fast in the mountains — pack layers and a rain shell year-round.",
      "A rental car is ideal for Glenorchy, Arrowtown and the wine valleys.",
    ],
  },
  {
    slug: "bangkok-thailand",
    name: "Bangkok",
    country: "Thailand",
    region: "Asia",
    tagline: "Gilded temples, street food and neon nights",
    image:
      "https://images.unsplash.com/photo-1731239027673-1a5c6cd61380?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-09-03",
    tripLength: "3–5 days",
    bestTime: "November–February (cool & dry)",
    budget: { budget: "฿900–1,500 / day", mid: "฿2,500–4,500 / day", luxury: "฿9,000+ / day" },
    intro:
      "The world's most-visited city is a heady collision of the sacred and the frenetic: golden temple spires rise above tuk-tuk gridlock, riverboats slice the Chao Phraya, and the greatest street food on earth sizzles on every corner. Bangkok rewards the curious — cool malls and speakeasy rooftop bars sit minutes from 200-year-old canals and dawn alms rounds. It is astonishing value, endlessly energetic and open very late.",
    areas: [
      { name: "Rattanakosin (Old City)", detail: "The historic royal island — Grand Palace, Wat Pho and Wat Arun across the river. Sight-dense and best early." },
      { name: "Sukhumvit", detail: "The modern spine: Skytrain-linked malls, rooftop bars, international dining and most mid-to-luxury hotels." },
      { name: "Silom & Riverside", detail: "Business by day, night markets and grand riverfront hotels by the Chao Phraya — great sunset drinks." },
      { name: "Banglamphu & Khao San", detail: "Backpacker legend turned buzzy nightlife strip, walkable to the old temples and cheap eats." },
    ],
    highlights: [
      "The Grand Palace and the Emerald Buddha",
      "Reclining Buddha and a traditional massage at Wat Pho",
      "Longtail boat through the Thonburi canals to Wat Arun",
      "Chatuchak Weekend Market — 15,000 stalls",
      "A rooftop bar sundowner over the skyline",
    ],
    itinerary: [
      { day: 1, title: "Old City temples", detail: "Grand Palace at opening, Wat Pho, then a ferry to Wat Arun and sunset drinks on the riverside." },
      { day: 2, title: "Markets & malls", detail: "Chatuchak (or floating market) in the morning, cool off in the Siam malls, and a street-food crawl in Chinatown (Yaowarat) at night." },
      { day: 3, title: "Canals & culture", detail: "Longtail boat through Thonburi's canals, Jim Thompson House, and a rooftop bar to finish." },
    ],
    tips: [
      "Use the BTS Skytrain and MRT to skip the notorious traffic; buy a rechargeable Rabbit card.",
      "Dress modestly for temples — covered shoulders and knees, shoes off inside.",
      "Agree tuk-tuk fares first, or use Grab for metered, hassle-free rides.",
      "Yaowarat (Chinatown) after dark is the city's best street-food street.",
    ],
  },
  {
    slug: "paris-france",
    name: "Paris",
    country: "France",
    region: "Europe",
    tagline: "Grand boulevards, café terraces and timeless art",
    image:
      "https://images.unsplash.com/photo-1654448190693-f59b70e5ee3c?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-09-03",
    tripLength: "3–5 days",
    bestTime: "April–June and September–October",
    budget: { budget: "€80–120 / day", mid: "€170–260 / day", luxury: "€450+ / day" },
    intro:
      "Paris needs little introduction: Haussmann façades, the Seine's bridges, world-defining museums and a café culture built for lingering. Beyond the icons it's a city of villages — market streets, hidden courtyards and neighbourhood bistros where a carafe of wine and a long lunch are the whole point. Walk it slowly, ride the Métro when your feet tire, and save room for pastry.",
    areas: [
      { name: "Le Marais", detail: "Medieval lanes, falafel, boutiques and museums (Picasso, Carnavalet) — central, lively and very walkable." },
      { name: "Saint-Germain & Latin Quarter", detail: "Left-bank literary Paris: bookshops, historic cafés, the Panthéon and the Luxembourg Gardens." },
      { name: "Montmartre", detail: "Hilltop village of Sacré-Cœur, artists' squares and winding steps with the city at your feet." },
      { name: "Champs-Élysées & 7th", detail: "Grand avenues, the Eiffel Tower and the great museums — polished, iconic and hotel-rich." },
    ],
    highlights: [
      "The Louvre and the Musée d'Orsay",
      "Eiffel Tower at golden hour, sparkling on the hour after dark",
      "Notre-Île and a Seine river cruise",
      "Montmartre and Sacré-Cœur",
      "A pastry and market crawl through a neighbourhood like Rue Cler",
    ],
    itinerary: [
      { day: 1, title: "Right-bank icons", detail: "Louvre in the morning, Tuileries and Place de la Concorde, then the Champs-Élysées to the Arc de Triomphe." },
      { day: 2, title: "Left bank & river", detail: "Musée d'Orsay, Saint-Germain cafés, Luxembourg Gardens, and a sunset Seine cruise past the Eiffel Tower." },
      { day: 3, title: "Marais & Montmartre", detail: "Marais boutiques and falafel for lunch, then Montmartre's steps and Sacré-Cœur at dusk." },
    ],
    tips: [
      "Buy museum tickets online with timed slots — the Louvre and Orsay queues are brutal otherwise.",
      "A carnet of Métro tickets or a Navigo Easy card beats buying singles.",
      "Many museums are free on the first Sunday of the month, but very busy.",
      "Lunch menus (formule) are far better value than dinner à la carte.",
    ],
  },
  {
    slug: "london-united-kingdom",
    name: "London",
    country: "United Kingdom",
    region: "Europe",
    tagline: "Royal history, world museums and buzzing markets",
    image:
      "https://images.unsplash.com/photo-1681407979872-0a4cbde28391?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-09-03",
    tripLength: "3–5 days",
    bestTime: "May–September (mild, long days)",
    budget: { budget: "£90–140 / day", mid: "£180–300 / day", luxury: "£500+ / day" },
    intro:
      "London is a city of villages stitched together by the Thames and the Tube — royal pageantry and cutting-edge art, centuries-old pubs and street-food markets, all in a dozen languages. Its world-class museums are free, its parks are vast, and each neighbourhood has its own character. Endlessly rewarding, it packs a continent's worth of culture into walkable pockets.",
    areas: [
      { name: "Westminster & South Bank", detail: "Big Ben, the Abbey, the London Eye and a riverside walk past the Tate Modern and Borough Market." },
      { name: "Soho & Covent Garden", detail: "Theatreland, restaurants and nightlife — central, buzzy and a smart first-visit base." },
      { name: "Shoreditch & East", detail: "Street art, vintage markets and the city's best bars and brunches; creative and lively." },
      { name: "South Kensington", detail: "The great free museums (V&A, Natural History, Science) and elegant, quieter garden squares." },
    ],
    highlights: [
      "British Museum and the Tate Modern (both free)",
      "Tower of London and Tower Bridge",
      "Westminster, the Abbey and a walk along the South Bank",
      "Borough Market for a food crawl",
      "A West End show and a historic pub",
    ],
    itinerary: [
      { day: 1, title: "Royal London", detail: "Westminster Abbey, Big Ben and the Changing of the Guard, then St James's Park and Trafalgar Square." },
      { day: 2, title: "City & river", detail: "Tower of London, Tower Bridge, walk the South Bank to the Tate Modern and Borough Market for lunch." },
      { day: 3, title: "Museums & markets", detail: "British Museum, Covent Garden, and Shoreditch's street art and bars in the evening." },
    ],
    tips: [
      "Tap in and out with a contactless card on the Tube and buses — it auto-caps the daily fare.",
      "The big national museums are free; special exhibitions are ticketed.",
      "Book the Tower of London and any West End shows ahead for the best prices.",
      "Stand on the right on Tube escalators, and mind the gap.",
    ],
  },
  {
    slug: "dubai-uae",
    name: "Dubai",
    country: "United Arab Emirates",
    region: "Middle East",
    tagline: "Sky-high ambition, desert dunes and gold souks",
    image:
      "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-09-03",
    tripLength: "3–5 days",
    bestTime: "November–March (warm & sunny)",
    budget: { budget: "AED 300–500 / day", mid: "AED 800–1,500 / day", luxury: "AED 3,500+ / day" },
    intro:
      "Dubai turns superlatives into a skyline: the world's tallest tower, artificial islands, ski slopes inside malls and beaches lapped by warm Gulf water. But scratch the glitz and there's an older city of spice and gold souks, wooden abra boats crossing the Creek, and a desert that glows at sunset. It's slick, sun-drenched and built for a comfortable, big-ticket short break.",
    areas: [
      { name: "Downtown & Burj Khalifa", detail: "The Burj, Dubai Mall and the Fountain shows — the glossy heart, hotel-rich and central." },
      { name: "Dubai Marina & JBR", detail: "Waterfront towers, a beach walk, yachts and buzzing restaurants — great for a first stay." },
      { name: "Deira & Bur Dubai (Old Dubai)", detail: "The Creek, the gold and spice souks and abra boat crossings — the atmospheric, better-value side." },
      { name: "Palm Jumeirah", detail: "The palm-shaped island of beach resorts, Atlantis and skyline views — resort luxury." },
    ],
    highlights: [
      "The top of Burj Khalifa and the Dubai Fountain show",
      "A desert safari with dunes, camels and a Bedouin dinner",
      "Gold and spice souks with an abra ride across the Creek",
      "Beach day at JBR or the Palm",
      "Old-meets-new views from the Dubai Frame",
    ],
    itinerary: [
      { day: 1, title: "Modern Dubai", detail: "Burj Khalifa at opening, Dubai Mall and aquarium, and the Fountain show after dark." },
      { day: 2, title: "Desert & souks", detail: "Old Dubai souks and an abra crossing by day, then an afternoon desert safari with a sunset dinner." },
      { day: 3, title: "Beach & the Palm", detail: "JBR beach morning, the Palm and Atlantis, and marina dining in the evening." },
    ],
    tips: [
      "The Metro is cheap and spotless; buy a Nol card. Taxis and Careem are plentiful too.",
      "Dress modestly away from beaches and pools, especially in Old Dubai and malls.",
      "Friday is the holy day — some sights open later; brunch is a Dubai institution.",
      "Summer (Jun–Sep) is extreme heat; November–March is ideal for being outdoors.",
    ],
  },
  {
    slug: "singapore",
    name: "Singapore",
    country: "Singapore",
    region: "Asia",
    tagline: "Futuristic gardens, hawker feasts and green skylines",
    image:
      "https://images.unsplash.com/photo-1620033263019-f2ec2c738a60?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-09-03",
    tripLength: "3–4 days",
    bestTime: "February–April (slightly drier)",
    budget: { budget: "S$70–110 / day", mid: "S$180–300 / day", luxury: "S$600+ / day" },
    intro:
      "A garden city from the future, Singapore pairs supertree groves and infinity pools with some of the world's best cheap eats. Spotless, safe and effortlessly multicultural, it layers Chinese, Malay, Indian and colonial heritage across walkable districts — and it's small enough to see the highlights in a few dense, delicious days. The hawker centres alone are worth the trip.",
    areas: [
      { name: "Marina Bay", detail: "The postcard core — Marina Bay Sands, Gardens by the Bay and the Merlion; dazzling after dark." },
      { name: "Chinatown & CBD", detail: "Temples, heritage shophouses and top hawker centres (Maxwell) beside the skyscrapers." },
      { name: "Kampong Glam & Little India", detail: "Arab Street's cafés and murals, the Sultan Mosque, and Little India's colour and spice." },
      { name: "Sentosa", detail: "The resort island — beaches, Universal Studios and cable-car views, a short hop south." },
    ],
    highlights: [
      "Gardens by the Bay and the Supertree light show",
      "The Marina Bay Sands SkyPark view",
      "A hawker-centre feast (Maxwell or Lau Pa Sat)",
      "Chinatown, Little India and Kampong Glam in a day",
      "Night Safari at the zoo",
    ],
    itinerary: [
      { day: 1, title: "Marina Bay", detail: "Gardens by the Bay, the Cloud Forest dome, the SkyPark view and the free evening light show." },
      { day: 2, title: "Heritage districts", detail: "Chinatown temples and Maxwell hawker lunch, then Little India and Kampong Glam by evening." },
      { day: 3, title: "Sentosa or zoo", detail: "Beaches and Universal Studios on Sentosa, or the acclaimed Night Safari to end." },
    ],
    tips: [
      "The MRT is fast, cheap and air-conditioned — tap in with a contactless card.",
      "Hawker centres are the best value and the best food; look for the longest local queues.",
      "It's hot and humid year-round with afternoon showers — carry water and a small umbrella.",
      "Chewing gum sales are restricted and littering fines are steep; the city is famously spotless.",
    ],
  },
  {
    slug: "kuala-lumpur-malaysia",
    name: "Kuala Lumpur",
    country: "Malaysia",
    region: "Asia",
    tagline: "Twin towers, jungle temples and endless food",
    image:
      "https://images.unsplash.com/photo-1597148543182-830ef7bbb904?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-09-03",
    tripLength: "2–4 days",
    bestTime: "May–July and December–February",
    budget: { budget: "RM120–200 / day", mid: "RM350–600 / day", luxury: "RM1,200+ / day" },
    intro:
      "Kuala Lumpur is Southeast Asia at its most delicious and affordable: the Petronas Towers glinting over colonial arcades, limestone caves draped in gold, and a food scene where Malay, Chinese and Indian kitchens collide gloriously. It's green, easy to navigate and superb value, with rooftop bars and megamalls a monorail ride from century-old temples and buzzing night markets.",
    areas: [
      { name: "KLCC & Bukit Bintang", detail: "The Petronas Towers, malls, rooftop bars and the Jalan Alor food street — the central, hotel-dense core." },
      { name: "Chinatown & Merdeka", detail: "Petaling Street market, colonial landmarks and old-town temples and coffee shops." },
      { name: "Bangsar & Brickfields", detail: "Bangsar's cafés and nightlife, Brickfields' Little India — local, tasty and lively." },
      { name: "Batu Caves (day-trip)", detail: "Golden Murugan statue and rainbow steps to Hindu cave temples, 30 minutes north by train." },
    ],
    highlights: [
      "Petronas Twin Towers and the skybridge",
      "Batu Caves' rainbow steps and shrines",
      "A street-food crawl on Jalan Alor",
      "Rooftop-bar skyline views at night",
      "Colonial Merdeka Square and Chinatown",
    ],
    itinerary: [
      { day: 1, title: "City icons", detail: "Petronas Towers and KLCC park, Bukit Bintang shopping, and a Jalan Alor food crawl at night." },
      { day: 2, title: "Old town & caves", detail: "Batu Caves in the morning, then Merdeka Square, Chinatown's Petaling Street and a rooftop sundowner." },
    ],
    tips: [
      "The LRT, MRT and monorail cover the centre cheaply; Grab is great for short hops.",
      "Cover shoulders and knees at Batu Caves and mosques; watch your snacks around the monkeys.",
      "Afternoon downpours are common — plan indoor breaks in the malls.",
      "Hawker and kopitiam food is the highlight and costs a few ringgit.",
    ],
  },
  {
    slug: "new-york-usa",
    name: "New York City",
    country: "United States",
    region: "North America",
    tagline: "Skyscrapers, neighbourhoods and non-stop energy",
    image:
      "https://images.pexels.com/photos/8569166/pexels-photo-8569166.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    updated: "2026-09-03",
    tripLength: "4–6 days",
    bestTime: "April–June and September–November",
    budget: { budget: "$120–180 / day", mid: "$250–400 / day", luxury: "$700+ / day" },
    intro:
      "The city that never sleeps delivers on the hype: a skyline you already know by heart, Central Park's green lung, museums that could each fill a trip, and a hundred distinct neighbourhoods to eat your way through. New York runs on energy and walking — cross a few blocks and you're in a different country, cuisine and mood. Loud, fast and utterly exhilarating.",
    areas: [
      { name: "Midtown", detail: "Times Square, the Empire State, Rockefeller and Broadway — dense, iconic and transit-central." },
      { name: "Lower Manhattan", detail: "The Financial District, 9/11 Memorial, Statue of Liberty ferries and trendy Tribeca and SoHo." },
      { name: "Greenwich Village & Chelsea", detail: "Brownstone charm, the High Line, Chelsea Market and the city's best low-key dining." },
      { name: "Brooklyn (Williamsburg/DUMBO)", detail: "Skyline views, waterfront parks, street food and a cooler, creative scene across the bridge." },
    ],
    highlights: [
      "Skyline views from the Empire State or Top of the Rock",
      "Central Park and the Metropolitan Museum of Art",
      "Statue of Liberty and Ellis Island ferry",
      "Walk the Brooklyn Bridge into DUMBO",
      "A Broadway show and a slice of pizza",
    ],
    itinerary: [
      { day: 1, title: "Midtown icons", detail: "Top of the Rock, Rockefeller and Fifth Avenue, Times Square by night and a Broadway show." },
      { day: 2, title: "Park & museums", detail: "Central Park stroll, the Met (or MoMA), and the Upper West Side in the evening." },
      { day: 3, title: "Downtown & Brooklyn", detail: "9/11 Memorial and the Financial District, walk the Brooklyn Bridge to DUMBO for skyline sunset." },
      { day: 4, title: "Villages & the High Line", detail: "Chelsea Market, the High Line, Greenwich Village and SoHo shopping." },
    ],
    tips: [
      "Get a 7-day unlimited MetroCard or tap OMNY with a contactless card for the subway.",
      "Museum admissions like the Met are pay-what-you-wish for NY State residents; timed tickets help everyone skip lines.",
      "Tipping is expected — around 18–20% in restaurants and bars.",
      "Walk when you can; crosstown traffic is slower than your own two feet.",
    ],
  },
  {
    slug: "tokyo-japan",
    name: "Tokyo",
    country: "Japan",
    region: "Asia",
    tagline: "Neon districts, ancient shrines and precision food",
    image:
      "https://images.unsplash.com/photo-1542052125323-e69ad37a47c2?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-09-03",
    tripLength: "4–6 days",
    bestTime: "March–April (blossom) and October–November (autumn)",
    budget: { budget: "¥9,000–14,000 / day", mid: "¥22,000–38,000 / day", luxury: "¥80,000+ / day" },
    intro:
      "Tokyo is the future and the past at once — serene shrines and centuries-old gardens tucked between neon canyons, robot precision beside deep tradition. It's the world's greatest food city (more Michelin stars than anywhere), impeccably clean and safe, and each district feels like its own city. Overwhelming and mesmerising, it rewards both meticulous plans and getting happily lost.",
    areas: [
      { name: "Shinjuku", detail: "Neon nightlife, skyscraper views, Golden Gai's tiny bars and the vast station — a buzzing base." },
      { name: "Shibuya & Harajuku", detail: "The famous scramble crossing, youth fashion, Meiji Shrine and Yoyogi Park side by side." },
      { name: "Asakusa & Ueno", detail: "Old Tokyo: Senso-ji temple, riverside streets and Ueno's museums and park." },
      { name: "Ginza & Marunouchi", detail: "Elegant shopping, department-store food halls and the Imperial Palace gardens — polished and central." },
    ],
    highlights: [
      "Senso-ji temple and Nakamise shopping street",
      "The Shibuya scramble crossing and Meiji Shrine",
      "Sushi and a food-hall crawl (Toyosu / depachika)",
      "TeamLab digital-art museum",
      "A day trip to Nikko or Hakone with Mt Fuji views",
    ],
    itinerary: [
      { day: 1, title: "Old Tokyo", detail: "Senso-ji in Asakusa, a Sumida river walk, and Ueno's park and museums." },
      { day: 2, title: "West side", detail: "Meiji Shrine, Harajuku and Shibuya crossing, then Shinjuku's skyscraper views and Golden Gai bars." },
      { day: 3, title: "Culture & neon", detail: "Imperial Palace gardens and Ginza by day, teamLab and Akihabara by night." },
      { day: 4, title: "Day trip", detail: "Hakone for hot springs and Mt Fuji, or Nikko's shrines and forests." },
    ],
    tips: [
      "Get a Suica/Pasmo IC card (or add it to your phone) for trains, buses and convenience stores.",
      "A Japan Rail Pass pays off only with intercity travel; within Tokyo, pay per ride.",
      "Carry some cash — smaller restaurants and shrines may not take cards.",
      "Trains stop around midnight; plan your last ride or expect a pricey taxi.",
    ],
  },
  {
    slug: "hong-kong",
    name: "Hong Kong",
    country: "Hong Kong",
    region: "Asia",
    tagline: "Harbour lights, dim sum and mountain-top views",
    image:
      "https://images.unsplash.com/photo-1577871598838-a543ee47cd79?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-09-03",
    tripLength: "3–4 days",
    bestTime: "October–December (cool, clear, dry)",
    budget: { budget: "HK$500–800 / day", mid: "HK$1,200–2,200 / day", luxury: "HK$5,000+ / day" },
    intro:
      "Few skylines thrill like Hong Kong's, best seen from the Peak or a Star Ferry crossing Victoria Harbour at dusk. It's a vertical city of dim sum parlours and neon signs, yet minutes away lie green peaks, fishing villages and beaches. East meets West at every turn — double-decker trams, temple incense and Michelin-starred street food — in one of the world's most exhilarating, efficient cities.",
    areas: [
      { name: "Central & The Peak", detail: "Skyscrapers, the tram up Victoria Peak, mid-levels escalators and Soho's bars and dining." },
      { name: "Tsim Sha Tsui (Kowloon)", detail: "The harbourfront promenade, Symphony of Lights view, museums and buzzing markets." },
      { name: "Sheung Wan & Sai Ying Pun", detail: "Antique shops, temples, cool cafés and the city's most characterful old streets." },
      { name: "Lantau & the Outlying Islands", detail: "The Big Buddha, cable car and fishing-village seafood — a nature-and-heritage day trip." },
    ],
    highlights: [
      "Victoria Peak tram and the skyline view",
      "Star Ferry across the harbour at dusk",
      "Dim sum at a classic tea house",
      "Big Buddha and the Ngong Ping cable car on Lantau",
      "Temple Street and Ladies' Market night markets",
    ],
    itinerary: [
      { day: 1, title: "Island & Peak", detail: "Central and the mid-levels, the Peak Tram for the view, and Soho dining in the evening." },
      { day: 2, title: "Kowloon & harbour", detail: "Tsim Sha Tsui promenade and museums, dim sum lunch, and the Symphony of Lights over the harbour." },
      { day: 3, title: "Lantau escape", detail: "Cable car to the Big Buddha and Po Lin Monastery, then Tai O fishing village for seafood." },
    ],
    tips: [
      "Get an Octopus card — it works on the MTR, trams, ferries, buses and in shops.",
      "The Star Ferry is one of the world's great cheap rides; take it at sunset.",
      "The Peak Tram gets busy — go early or book a timed ticket.",
      "Autumn is clearest for views; summer brings heat, humidity and the odd typhoon.",
    ],
  },
  {
    slug: "barcelona-spain",
    name: "Barcelona",
    country: "Spain",
    region: "Europe",
    tagline: "Gaudí fantasy, beaches and late-night tapas",
    image:
      "https://images.unsplash.com/photo-1650964827770-421afa7960ac?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-09-03",
    tripLength: "3–4 days",
    bestTime: "May–June and September–October",
    budget: { budget: "€70–110 / day", mid: "€150–240 / day", luxury: "€420+ / day" },
    intro:
      "Barcelona is where Mediterranean beach life meets wild imagination: Gaudí's dreamlike Sagrada Família and Park Güell, a Gothic old town of hidden squares, and a seafront of golden sand and seafood. Add vermouth hours, late tapas dinners and a fierce local identity, and you have one of Europe's most seductive city breaks — walkable, sunny and full of colour.",
    areas: [
      { name: "Barri Gòtic & El Born", detail: "The medieval maze — the cathedral, tapas bars, the Picasso Museum and the city's oldest lanes." },
      { name: "Eixample", detail: "Gaudí's Sagrada Família and Casa Batlló on grand grid boulevards — elegant and central." },
      { name: "Gràcia", detail: "A village-like barrio of leafy squares, indie shops and vermouth bars near Park Güell." },
      { name: "Barceloneta & the beach", detail: "The seafront quarter — sand, seafood shacks and a lively boardwalk." },
    ],
    highlights: [
      "La Sagrada Família and Park Güell",
      "Gothic Quarter wander and the cathedral",
      "La Boqueria market and a tapas crawl",
      "Casa Batlló and La Pedrera on Passeig de Gràcia",
      "Sunset from Bunkers del Carmel over the city",
    ],
    itinerary: [
      { day: 1, title: "Gaudí day", detail: "Sagrada Família at opening, Passeig de Gràcia's Modernista houses, then Park Güell for sunset." },
      { day: 2, title: "Old town & sea", detail: "Gothic Quarter and El Born, La Boqueria for lunch, and Barceloneta beach in the afternoon." },
      { day: 3, title: "Views & vermouth", detail: "Montjuïc's gardens and Magic Fountain, or Gràcia's squares and a long tapas-and-vermouth evening." },
    ],
    tips: [
      "Book Sagrada Família and Park Güell online with timed entry — they sell out.",
      "Watch belongings on La Rambla and the metro; pickpocketing is the main nuisance.",
      "Dinner starts late (9pm+); bridge the gap with vermouth and tapas.",
      "A T-casual metro ticket (10 rides) is the cheapest way around.",
    ],
  },
  {
    slug: "seoul-south-korea",
    name: "Seoul",
    country: "South Korea",
    region: "Asia",
    tagline: "Palaces, K-pop energy and midnight street food",
    image:
      "https://images.unsplash.com/photo-1570191913384-7b4ff11716e7?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-09-03",
    tripLength: "4–5 days",
    bestTime: "April–May and September–October",
    budget: { budget: "₩70,000–110,000 / day", mid: "₩180,000–300,000 / day", luxury: "₩600,000+ / day" },
    intro:
      "Seoul runs at hyperspeed yet never loses its soul: Joseon-era palaces and hanok villages sit beneath neon towers, and the world's fastest wifi coexists with 600-year-old markets. It's a city of late-night barbecue, glowing convenience stores, K-beauty flagships and mountain temples minutes from the subway — endlessly modern, deeply traditional and superb value for a food-first city break.",
    areas: [
      { name: "Myeongdong & Jung-gu", detail: "Central shopping and street food, walkable to Gyeongbokgung and the palaces — a convenient base." },
      { name: "Bukchon & Insadong", detail: "Hanok alleys, tea houses, galleries and traditional crafts between the two main palaces." },
      { name: "Hongdae", detail: "University nightlife, indie music, buskers and cheap eats — young, loud and fun after dark." },
      { name: "Gangnam & Itaewon", detail: "Upscale dining and nightlife south of the river; Itaewon for international food and bars." },
    ],
    highlights: [
      "Gyeongbokgung Palace and the guard-changing ceremony",
      "Bukchon Hanok Village lanes",
      "N Seoul Tower on Namsan at sunset",
      "A Korean BBQ and late-night pojangmacha crawl",
      "Gwangjang Market street food",
    ],
    itinerary: [
      { day: 1, title: "Palaces & hanok", detail: "Gyeongbokgung and the ceremony, Bukchon Hanok Village, Insadong tea and Gwangjang Market for dinner." },
      { day: 2, title: "Modern Seoul", detail: "Dongdaemun design plaza, Myeongdong shopping and K-beauty, then N Seoul Tower at sunset." },
      { day: 3, title: "Nightlife & river", detail: "Hongdae by day, a Han River park picnic, and Korean BBQ with soju in the evening." },
    ],
    tips: [
      "Grab a T-money card for the fast, cheap subway and buses.",
      "Rent a hanbok near the palaces for free entry and great photos.",
      "Convenience stores (CU, GS25) are a cheap 24/7 lifeline for snacks and meals.",
      "Download Naver Map or KakaoMap — Google Maps navigation is limited in Korea.",
    ],
  },
  {
    slug: "osaka-japan",
    name: "Osaka",
    country: "Japan",
    region: "Asia",
    tagline: "Neon canals, street food and easygoing charm",
    image:
      "https://images.unsplash.com/photo-1491884592576-38221bd4314a?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-09-03",
    tripLength: "3–4 days",
    bestTime: "March–April (blossom) and October–November",
    budget: { budget: "¥8,000–13,000 / day", mid: "¥20,000–34,000 / day", luxury: "¥70,000+ / day" },
    intro:
      "Japan's kitchen is louder, friendlier and hungrier than its neighbours — Osaka lives to eat. Neon reflects off the Dotonbori canal, takoyaki sizzles on every corner, and a castle rises over the modern sprawl. Warm and unpretentious, it's the perfect base for Kyoto and Nara day trips, with a nightlife and street-food scene that's second to none.",
    areas: [
      { name: "Namba & Dotonbori", detail: "The neon heart — the Glico sign, street food and nightlife on the canal; the classic base." },
      { name: "Umeda (Kita)", detail: "Skyscrapers, the Floating Garden observatory, department-store food halls and transport hub." },
      { name: "Shinsekai", detail: "Retro-kitsch old district under Tsutenkaku Tower — cheap kushikatsu and old-school Osaka." },
      { name: "Osaka Castle & around", detail: "The landmark castle and its park; cherry blossoms and history a short metro ride out." },
    ],
    highlights: [
      "Dotonbori's neon and the Glico running man",
      "Osaka Castle and its park",
      "A takoyaki and kushikatsu street-food crawl",
      "Kuromon Ichiba fresh-food market",
      "Day trip to Nara's deer and Kyoto's temples",
    ],
    itinerary: [
      { day: 1, title: "Eat the city", detail: "Osaka Castle in the morning, Kuromon Market for lunch, and Dotonbori/Namba street food and neon at night." },
      { day: 2, title: "Nara day trip", detail: "Todai-ji's great Buddha and the bowing deer of Nara Park, back for Shinsekai kushikatsu." },
      { day: 3, title: "Kyoto or Universal", detail: "Fushimi Inari and Kiyomizu-dera in Kyoto, or a day at Universal Studios Japan." },
    ],
    tips: [
      "Get an ICOCA card for trains; the Osaka Amazing Pass bundles transit and free sights.",
      "Kyoto and Nara are 30–45 min away — easy half or full-day trips.",
      "Dotonbori is busiest and best at night; come hungry.",
      "Stand on the RIGHT of escalators in Osaka (the opposite of Tokyo).",
    ],
  },
  {
    slug: "florence-italy",
    name: "Florence",
    country: "Italy",
    region: "Europe",
    tagline: "Renaissance art, Tuscan food and golden light",
    image:
      "https://images.unsplash.com/photo-1528114039593-4366cc08227d?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-09-03",
    tripLength: "2–4 days",
    bestTime: "April–June and September–October",
    budget: { budget: "€70–110 / day", mid: "€150–240 / day", luxury: "€420+ / day" },
    intro:
      "The cradle of the Renaissance is a walkable open-air museum: Brunelleschi's dome crowns a skyline of terracotta roofs, the Uffizi holds Botticelli and Michelangelo, and the Arno glows gold at dusk from the Ponte Vecchio. Add Tuscan trattorias, gelato and easy day trips into Chianti's vineyards, and Florence is a compact, art-soaked escape — and a perfect pairing with nearby Rome.",
    areas: [
      { name: "Duomo & Centro", detail: "The cathedral, Baptistery and the main squares — the walkable historic heart, sight-dense and central." },
      { name: "Oltrarno & San Niccolò", detail: "Across the river: artisan workshops, Pitti Palace, Boboli Gardens and a more local, evening buzz." },
      { name: "Santa Croce", detail: "The great basilica, leather markets and lively trattorias just east of the centre." },
      { name: "San Lorenzo & Mercato Centrale", detail: "The market district — the food hall, street stalls and the Medici chapels." },
    ],
    highlights: [
      "Climb Brunelleschi's Duomo for the rooftop view",
      "The Uffizi Gallery and Michelangelo's David (Accademia)",
      "Sunset over the Ponte Vecchio",
      "Piazzale Michelangelo panorama",
      "A Chianti wine day trip into Tuscany",
    ],
    itinerary: [
      { day: 1, title: "Renaissance core", detail: "Duomo and its climb, the Baptistery, Piazza della Signoria and the Uffizi in the afternoon." },
      { day: 2, title: "David & the Oltrarno", detail: "The Accademia for David, cross to the Oltrarno for Pitti Palace and Boboli, sunset at Piazzale Michelangelo." },
      { day: 3, title: "Tuscan escape", detail: "A Chianti wine tour or a day in Siena and San Gimignano among the hill towns." },
    ],
    tips: [
      "Book the Uffizi, Accademia and Duomo dome climb online with timed slots — they sell out.",
      "The centre is compact and walkable; you rarely need transport.",
      "Trattorias fill up — reserve, or eat the early Italian dinner around 7:30pm.",
      "Florence is 1.5h from Rome by fast train — an easy add-on to a Rome trip.",
    ],
  },
  {
    slug: "bali-beaches-indonesia",
    name: "Bali Beaches",
    country: "Indonesia",
    region: "Asia",
    tagline: "Surf breaks, cliff temples and sunset beach clubs",
    image:
      "https://images.unsplash.com/photo-1604842937405-b2b9eada7c97?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
    updated: "2026-09-03",
    tripLength: "5–7 days",
    bestTime: "April–October (dry season)",
    budget: { budget: "IDR 500k–900k / day", mid: "IDR 1.5m–3m / day", luxury: "IDR 7m+ / day" },
    intro:
      "Where Ubud is jungle and rice terraces, Bali's south is all ocean: golden surf beaches, dramatic clifftop temples and some of the world's best beach clubs. From laid-back Canggu to buzzy Seminyak and the limestone cliffs of Uluwatu, this is the island's sun-and-surf side — warm water, world-class sunsets, and long days that drift from morning waves to sundowner cocktails.",
    areas: [
      { name: "Seminyak", detail: "Chic beach clubs, boutiques and the best sunset dining — polished and central to the south." },
      { name: "Canggu", detail: "Surf breaks, café culture and a young digital-nomad scene — relaxed and great value." },
      { name: "Uluwatu", detail: "Clifftop temples, hidden coves and epic surf on the Bukit Peninsula — dramatic and scenic." },
      { name: "Jimbaran & Nusa Dua", detail: "Calm bays, seafood on the sand and resort comfort — family- and luxury-friendly." },
    ],
    highlights: [
      "Uluwatu Temple and the Kecak fire dance at sunset",
      "Surf or beach-club day in Canggu and Seminyak",
      "Seafood grill on Jimbaran Bay",
      "Day trip to Nusa Penida's cliffs and beaches",
      "Snorkelling or diving off the south coast",
    ],
    itinerary: [
      { day: 1, title: "Seminyak sunsets", detail: "Beach morning, boutique browsing, and a beach-club sundowner with dinner on the sand." },
      { day: 2, title: "Uluwatu & the Bukit", detail: "Padang Padang beach, clifftop cafés, and Uluwatu Temple's Kecak dance at sunset." },
      { day: 3, title: "Canggu & surf", detail: "A surf lesson or café-hop in Canggu, then a laid-back beach bar for the evening." },
      { day: 4, title: "Island day trip", detail: "Fast boat to Nusa Penida for Kelingking cliff and snorkelling with manta rays." },
    ],
    tips: [
      "Hire a scooter or use Grab/Gojek; south Bali traffic is heavy, so plan around it.",
      "Cover shoulders and wear a sarong at temples (often provided at the gate).",
      "Dry season (Apr–Oct) has the best surf and sunsets; carry reef-safe sunscreen.",
      "Book Nusa Penida day trips and popular beach clubs ahead in high season.",
    ],
  },
];

export const findDestination = (slug) => DESTINATIONS.find((d) => d.slug === slug);
